import { ActionSheetProvider } from '@expo/react-native-action-sheet';
import { BottomSheetModalProvider } from '@gorhom/bottom-sheet';
import { atom, useAtom } from 'jotai';
import { useEffect } from 'react';
import { type ColorSchemeName } from 'react-native';
import { GestureHandlerRootView } from 'react-native-gesture-handler';
import {
    isWeb,
    smoothieTamaguiConfig,
    type TamaguiInternalConfig,
    TamaguiPortalProvider,
    TamaguiProvider,
    Toast,
    ToastProvider,
    ToastViewport,
    TooltipGroupProvider,
} from 'smoothie';

import { captureWarning } from '../../logger/Logger';
import { useCustomFonts } from '../hooks/useCustomFonts';
import type { ContainerComponent } from '../types/Common';

type LightOrDarkTheme = Extract<ColorSchemeName, 'dark' | 'light'>;
const DEFAULT_THEME_NAME = 'light' as LightOrDarkTheme;

export const activeThemeAtom = atom<LightOrDarkTheme | null>(null);

export const useActiveTheme = (): [
    LightOrDarkTheme,
    (theme: LightOrDarkTheme) => void,
] => {
    const [activeTheme, setActiveTheme] = useAtom(activeThemeAtom);

    // When we want to support dark mode, we can use the following code
    // const scheme = useColorScheme();
    // and update the setter below to return this when the prev value is null: scheme === 'dark' ? 'dark' : 'light';

    useEffect(() => {
        setActiveTheme((prev) => {
            if (prev === null) {
                return DEFAULT_THEME_NAME;
            }
            return prev;
        });
    }, []);

    return [activeTheme ?? DEFAULT_THEME_NAME, setActiveTheme]; // asserts light theme unless we have a manual override
};

export const DesignSystemProvider: ContainerComponent = ({ children }) => {
    const [activeTheme] = useActiveTheme();
    const [, fontLoadingError] = useCustomFonts();

    useEffect(() => {
        if (fontLoadingError !== null) {
            captureWarning(
                'Error encountered while loading custom fonts',
                fontLoadingError,
            );
        }
    }, [fontLoadingError !== null]);

    return (
        <GestureHandlerRootView>
            <TamaguiProvider
                config={smoothieTamaguiConfig as TamaguiInternalConfig}
                defaultTheme={activeTheme}
            >
                {/* Sheets and modals render through the portal hosts below, so the toast
                    context has to be provided above them or `useToastController()` inside
                    sheet content resolves to an empty context. There must only ever be one
                    ToastViewport named 'default' mounted, since portal hosts are keyed by
                    name and a second one renders every toast twice on native. */}
                <ToastProvider>
                    <TamaguiPortalProvider shouldAddRootHost>
                        <ActionSheetProvider>
                            <BottomSheetModalProvider>
                                {children}
                                <TooltipGroupProvider />
                            </BottomSheetModalProvider>
                        </ActionSheetProvider>
                        {/* Rendered after the sheet portal host so toasts paint above an open sheet */}
                        <Toast />
                        <ToastViewport
                            portalToRoot={isWeb}
                            flexDirection="column"
                            bottom={120}
                            left={0}
                            right={0}
                            $sm={{ bottom: '90vh' }}
                        />
                    </TamaguiPortalProvider>
                </ToastProvider>
            </TamaguiProvider>
        </GestureHandlerRootView>
    );
};
